import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { liveChatPlans, specialOfferPlan, type ChatPlan } from "@/features/chat"
import { useLogger, ANALYTICS_EVENTS } from "@/features/analytics"
import { cn } from "@/lib/utils"

import { useCreditsPurchase } from "../hooks/use-credits-purchase"

import StarIcon from "@/assets/img/CreditsV4.webp"
import BackgroundImage from "@/assets/img/live-chat/stars_background.webp"
import SpecialOfferImage from "@/assets/img/live-chat/special_offer_image.webp"
import StarsBundle1 from "@/assets/img/live-chat/stars_bundles_1.webp"
import StarsBundle2 from "@/assets/img/live-chat/stars_bundles_2.webp"
import StarsBundle3 from "@/assets/img/live-chat/stars_bundles_3.webp"
import StarsBundle4 from "@/assets/img/live-chat/stars_bundles_4.webp"
import CheckedIcon from "@/assets/img/live-chat/plan_checked_icon.svg?react"
import LeafDecor from "@/assets/img/live-chat/leaf_decor.svg?react"
import CloseIcon from "@/assets/img/live-chat/x_close_icon.svg?react"
import ArrowMicro from "@/assets/svg/subscription/arrow_micro_new.svg?react"

export interface CreditsPurchaseModalProps {
  isOpen: boolean
  onClose: () => void
  /** Called when a credit purchase succeeds (before close/navigate). */
  onPurchased?: () => void
  userMainSubscriptionId: string | null
  variant?: "main-page" | "chat-page"
}

const bundleImages = [StarsBundle1, StarsBundle2, StarsBundle3, StarsBundle4]

/** Star-credits paywall (ported from PurchaseV4), charging the stored payment method. */
export function CreditsPurchaseModal({
  isOpen,
  onClose,
  onPurchased,
  userMainSubscriptionId,
  variant = "main-page",
}: CreditsPurchaseModalProps) {
  const navigate = useNavigate()
  const { trackAppyCntEvent } = useLogger()
  const { purchase, isLoading, error } = useCreditsPurchase({ userMainSubscriptionId })
  const [selectedPlan, setSelectedPlan] = useState<ChatPlan>(liveChatPlans[1])
  const [showSpecialOffer, setShowSpecialOffer] = useState(false)

  if (!isOpen) return null

  const handleSuccess = () => {
    onPurchased?.()
    if (variant === "main-page") {
      navigate({ to: WebappRoutes.READINGS })
    } else {
      onClose()
    }
  }

  const handleSkip = () => {
    onClose()
    trackAppyCntEvent(ANALYTICS_EVENTS.PAYWALL_SHOW, undefined)
    setShowSpecialOffer(false)
    if (variant === "chat-page") navigate({ to: WebappRoutes.ADVISORS })
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center">
      {error && (
        <div className="absolute left-1/2 top-6 z-[110] w-[calc(100%-32px)] max-w-app -translate-x-1/2 rounded-xl bg-peach px-4 py-3 text-center font-body text-sm text-white">
          {error}
        </div>
      )}

      {showSpecialOffer ? (
        <div className="relative flex w-full max-w-[450px] flex-col items-center rounded-t-3xl bg-dark-surface px-4 pb-4 pt-2">
          <button
            type="button"
            onClick={handleSkip}
            className="absolute right-4 top-4 [&_svg]:size-6"
            aria-label="Close"
          >
            <CloseIcon />
          </button>
          <img src={SpecialOfferImage} alt="stars" width={304} />
          <span className="flex items-center rounded-[15px] bg-green/20 px-3 py-2 font-body text-[10px] font-medium uppercase text-green">
            Very special offer!
          </span>
          <p className="my-2 text-center font-display text-2xl font-medium text-purple-light">
            Get {specialOfferPlan.creditsCount} credits just for
            <br />
            <span className="mr-2 text-purple-light/40 line-through decoration-peach">
              ${specialOfferPlan.unitAmountNoDiscount}
            </span>
            ${specialOfferPlan.unitAmount}
          </p>
          <p className="mb-4 text-center font-body text-base text-purple-light/70">
            Jupiter - the planet of luck and abundance - is shining bright in your chart this week.
            So it's time to find all the answers at a discounted price!
          </p>
          <PurchaseButton
            isLoading={isLoading}
            label="Continue"
            onClick={() => purchase(specialOfferPlan, handleSuccess)}
          />
        </div>
      ) : (
        <div className="relative w-full max-w-[450px] overflow-hidden rounded-t-3xl bg-dark-surface">
          <img src={BackgroundImage} alt="" className="absolute left-0 top-0" />
          <div className="relative z-[5] px-4 pb-4 pt-36">
            <p className="mb-2 text-center font-display text-2xl font-medium text-purple-light">
              {variant === "main-page" ? "Not Enough Stars" : "Want to keep chatting?"}
            </p>
            <p className="text-center font-body text-base text-purple-light/70">
              Don’t let your questions hang — chat now for clarity on love, career, and what lies
              ahead
            </p>

            <div className="my-4 flex flex-col gap-2">
              {liveChatPlans.map((plan, index) => {
                const isSelected = selectedPlan.code === plan.code
                return (
                  <button
                    type="button"
                    key={plan.code || index}
                    onClick={() => setSelectedPlan(plan)}
                    className={cn(
                      "flex w-full items-center gap-1 rounded-xl py-2 pl-2 pr-4 text-left",
                      isSelected
                        ? "bg-main-purple/10 outline outline-2 outline-main-purple"
                        : "outline outline-1 outline-purple-light/20",
                    )}
                  >
                    <img src={bundleImages[index] ?? StarsBundle4} alt="stars" width={44} />
                    <div className="w-full">
                      <p className="font-body text-base font-semibold text-white">
                        {plan.creditsCount} stars
                      </p>
                      <div className="flex items-center gap-1.5 font-body">
                        {plan.discount === 0 ? (
                          <span className="text-xs text-purple-light/70">
                            ${plan.unitAmountNoDiscount}
                          </span>
                        ) : (
                          <>
                            <span className="text-xs text-purple-light/40 line-through decoration-peach">
                              ${plan.unitAmountNoDiscount}
                            </span>
                            <span className="[&_svg]:size-3">
                              <ArrowMicro />
                            </span>
                            <span className="text-xs text-purple-light/70">${plan.unitAmount}</span>
                            <span className="flex h-5 items-center rounded-full bg-green/20 px-2 text-[10px] font-semibold text-green">
                              {plan.discount}% OFF
                            </span>
                            {plan.isPopular && (
                              <span className="flex h-5 items-center gap-1 rounded-full bg-gold/20 px-1.5 text-[10px] font-medium text-gold [&_svg]:h-3.5 [&_svg]:w-1.5 [&_svg:last-child]:-scale-x-100">
                                <LeafDecor />
                                POPULAR
                                <LeafDecor />
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                    {isSelected ? (
                      <span className="[&_svg]:size-6">
                        <CheckedIcon />
                      </span>
                    ) : (
                      <span className="size-6 min-w-6 rounded-full border border-purple-light/20" />
                    )}
                  </button>
                )
              })}
            </div>

            <div className="mb-4 flex items-center justify-center gap-1 font-body text-sm text-purple-light/70">
              <img src={StarIcon} alt="Stars" width={18} height={18} />
              60 stars = 1 min of consultation
            </div>

            <div className="h-px w-full bg-purple-light/10" />

            <div className="my-4 flex flex-row items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => setShowSpecialOffer(true)}
                className="h-[60px] w-full rounded-[80px] bg-purple-light/10 font-body text-base font-semibold text-purple-light"
              >
                Finish reading
              </button>
              <PurchaseButton
                isLoading={isLoading}
                label="Keep chatting"
                onClick={() => purchase(selectedPlan, handleSuccess)}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function PurchaseButton({
  isLoading,
  label,
  onClick,
}: {
  isLoading: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={isLoading}
      className="flex h-14 w-full items-center justify-center gap-1 rounded-[50px] bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white transition-opacity hover:opacity-80 disabled:bg-none disabled:bg-purple-light/10 disabled:text-purple-light/70"
    >
      {isLoading ? (
        <>
          <span className="size-5 animate-spin rounded-full border-2 border-purple-light border-t-transparent" />
          Purchasing...
        </>
      ) : (
        label
      )}
    </button>
  )
}
